/**
 * Loved Ones — core behaviour. No dependencies.
 *
 * Exposes `window.LovedOnes`:
 *   LovedOnes.motion            → true when playful motion is allowed
 *   LovedOnes.cart.add(form)    → POST /cart/add.js, then refresh + emit `cart:change`
 *   LovedOnes.cart.change(key, quantity)
 *   LovedOnes.cart.get()
 *   LovedOnes.flyTo(sourceEl, targetEl)  → clones an image and arcs it into the target
 *
 * Events on document:
 *   `cart:change` detail: { cart, source }   — after any successful cart mutation
 *   `cart:error`  detail: { message, source }
 *   `cart:open`                                — request the cart drawer to open
 */
(() => {
  const root = document.documentElement;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionSetting = root.dataset.motion !== 'off';

  function syncMotion() {
    const ok = motionSetting && !reduceQuery.matches;
    root.classList.toggle('motion-ok', ok);
    LovedOnes.motion = ok;
  }

  const LovedOnes = (window.LovedOnes = window.LovedOnes || {});
  syncMotion();
  reduceQuery.addEventListener('change', syncMotion);

  /* ─── Reveal on scroll ─────────────────────────────────── */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        revealObserver.unobserve(entry.target);
        reveal(entry.target);
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
  );

  function reveal(el) {
    el.classList.add('is-revealed');
    // After the spring settles, swap to hover-friendly transitions.
    // transitionend bubbles from children and fires per property — wait for this element's last one.
    const lastProperty = el.dataset.reveal === 'fade' ? 'opacity' : 'transform';
    const settle = (event) => {
      if (event.target !== el || event.propertyName !== lastProperty) return;
      el.classList.add('is-settled');
      el.removeEventListener('transitionend', settle);
    };
    el.addEventListener('transitionend', settle);
  }

  function observeReveals(scope = document) {
    const fold = window.innerHeight * 0.88;
    scope.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
      // In the theme editor, never hide content behind a reveal the owner has to scroll to see.
      if (!LovedOnes.motion || window.Shopify?.designMode) {
        el.classList.add('is-revealed', 'is-settled');
        return;
      }
      // Already above the fold: start now rather than waiting on an observer callback
      // (those are delayed in background tabs and on slow devices).
      const rect = el.getBoundingClientRect();
      if (rect.top < fold && rect.bottom > 0) {
        reveal(el);
        return;
      }
      revealObserver.observe(el);
    });
  }
  LovedOnes.observeReveals = observeReveals;

  /* ─── Lucky looks toward the pointer ───────────────────── */
  function initLook() {
    const lookers = [...document.querySelectorAll('.lucky[data-look]')];
    if (!lookers.length || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    let frame = 0;
    window.addEventListener(
      'pointermove',
      (event) => {
        if (!LovedOnes.motion || frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          for (const el of lookers) {
            const rect = el.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > innerHeight) continue;
            const dx = (event.clientX - (rect.left + rect.width / 2)) / innerWidth;
            const dy = (event.clientY - (rect.top + rect.height / 2)) / innerHeight;
            el.style.setProperty('--look-x', Math.max(-1, Math.min(1, dx * 2)).toFixed(3));
            el.style.setProperty('--look-y', Math.max(-1, Math.min(1, dy * 2)).toFixed(3));
          }
        });
      },
      { passive: true }
    );
  }

  /* ─── Cart API ─────────────────────────────────────────── */
  const routes = window.Shopify?.routes?.root || '/';

  async function request(path, body) {
    const response = await fetch(`${routes}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart request failed');
    }
    return data;
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  LovedOnes.cart = {
    get: () => request('cart.js'),
    async add(form, source) {
      try {
        await request('cart/add.js', form instanceof FormData ? form : new FormData(form));
        const cart = await request('cart.js');
        emit('cart:change', { cart, source });
        return cart;
      } catch (error) {
        emit('cart:error', { message: error.message, source });
        throw error;
      }
    },
    async change(key, quantity, source) {
      try {
        const cart = await request('cart/change.js', { id: key, quantity });
        emit('cart:change', { cart, source });
        return cart;
      } catch (error) {
        emit('cart:error', { message: error.message, source });
        throw error;
      }
    },
  };

  /* ─── Cart count in the header ─────────────────────────── */
  document.addEventListener('cart:change', ({ detail }) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const next = String(detail.cart.item_count);
      if (el.textContent === next) return;
      el.textContent = next;
      bump(el);
    });
  });

  function bump(el) {
    el.classList.remove('is-bumping');
    void el.offsetWidth; // restart the keyframe
    el.classList.add('is-bumping');
    el.addEventListener('animationend', () => el.classList.remove('is-bumping'), { once: true });
  }
  LovedOnes.bump = bump;

  /* ─── Fly-to-cart ──────────────────────────────────────── */
  /** Clones `sourceEl` (an <img>) and arcs it into `targetEl`. Resolves when it lands. */
  LovedOnes.flyTo = (sourceEl, targetEl) => {
    if (!LovedOnes.motion || !sourceEl || !targetEl || !sourceEl.animate) return Promise.resolve();
    const from = sourceEl.getBoundingClientRect();
    const to = targetEl.getBoundingClientRect();
    if (!from.width || !to.width) return Promise.resolve();

    const ghost = sourceEl.cloneNode();
    ghost.removeAttribute('srcset');
    ghost.src = sourceEl.currentSrc || sourceEl.src;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      objectFit: 'cover',
      borderRadius: '12px',
      zIndex: 1000,
      pointerEvents: 'none',
      margin: 0,
      viewTransitionName: 'none',
    });
    document.body.appendChild(ghost);

    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const scale = Math.max(0.06, 36 / from.width);
    const animation = ghost.animate(
      [
        { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1 },
        { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 80}px) scale(${(1 + scale) / 2.4}) rotate(-12deg)`, opacity: 1, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale}) rotate(-24deg)`, opacity: 0.4 },
      ],
      { duration: 620, easing: 'cubic-bezier(0.77, 0, 0.175, 1)' }
    );
    return animation.finished.then(() => {
      ghost.remove();
      bump(targetEl);
    });
  };

  /* ─── Boot ─────────────────────────────────────────────── */
  function boot() {
    observeReveals();
    initLook();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-run reveals when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));
})();
